/**
 * Client-side preference for conversation message layout.
 * Stored in localStorage; mirrors the pattern used by notification sound prefs.
 *
 * Bubble layout was removed — stored `bubble` values are migrated to `linear`
 * on read so existing installs pick up Linear on next load.
 */

import { useSyncExternalStore } from 'react';

export type MessageLayout = 'linear';

const STORAGE_KEY = 'adieuu.app.messageLayout';
const DEFAULT_LAYOUT: MessageLayout = 'linear';

const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) {
    l();
  }
}

function migrateStoredLayout(raw: string | null): MessageLayout {
  if (raw === 'linear') return 'linear';
  // Legacy `bubble` (and any other value) → linear.
  if (raw != null && raw !== 'linear') {
    localStorage.setItem(STORAGE_KEY, DEFAULT_LAYOUT);
  }
  return DEFAULT_LAYOUT;
}

export function getMessageLayout(): MessageLayout {
  return migrateStoredLayout(localStorage.getItem(STORAGE_KEY));
}

export function setMessageLayout(layout: MessageLayout): void {
  localStorage.setItem(STORAGE_KEY, layout);
  emit();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): MessageLayout {
  return getMessageLayout();
}

export function useMessageLayoutPreference(): MessageLayout {
  return useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_LAYOUT);
}

/** Migrate legacy Bubble preference on first import / app load. */
try {
  migrateStoredLayout(localStorage.getItem(STORAGE_KEY));
} catch {
  // Ignore missing localStorage (SSR / restricted environments).
}
